import {ChangeEvent, useMemo} from 'react'
import {Search} from '../model/SearchFilter'
import {useGetDisplayFiltersQuery, useSearchItemsQuery} from '../rtkSearchApi'
import {useSearchResultControl} from './useSearchResultControl'
import {useFullSearchParams} from './useFullSearchParams'

export const useFullSearch = ({
                                  overrideSearch,
                                  useFilters
                              }: {
    overrideSearch?: Search,
    useFilters?: boolean
}) => {
    const fullSearchParams = useFullSearchParams()

    const {
        page,
        pageSize,
        setPage,
        phrase,
        partNumberPhrase,
        labelPhrase,
        idPhrase,
        filters,
        itemTypes,
        organizations,
        notDefinedByOrganizations,
        technicalClassificationNodeId,
        isPublic,
        visibleByGuildId,
        managedByGuildId,
        guildId,
        itemListId,
        sortingDirection,
        sortingField,
        sortingDefinitionId
    } = fullSearchParams

    const search: Search = {
        phrase: phrase ? {phraseDetail: phrase, isExact: false} : null,
        technicalClassificationNodeId: technicalClassificationNodeId,
        partNumberPhrase: partNumberPhrase ? {phraseDetail: partNumberPhrase, isExact: false} : null,
        labelPhrase: labelPhrase ? {phraseDetail: labelPhrase, isExact: false} : null,
        idPhrase: idPhrase ? {phraseDetail: idPhrase, isExact: false} : null,
        guildId: guildId,
        filters: filters,
        itemListId: itemListId,
        itemTypeFilter: itemTypes,
        organizationFilter: organizations,
        notDefinedByOrganizationsFilter: notDefinedByOrganizations,
        visibleByGuildId: visibleByGuildId,
        isPublicFilter: isPublic,
        managedByGuildFilter: managedByGuildId,
        ...overrideSearch
    }

    const {data: searchResult, isFetching: isSearchFetching} = useSearchItemsQuery({
        search: {
            ...search,
            sorting: {
                direction: sortingDirection ?? 'asc',
                field: sortingField,
                ...sortingDefinitionId && {definitionId: sortingDefinitionId}
            }
        },
        pageNumber: page - 1,
        pageSize: pageSize
    })

    // Filters
    const {
        data: searchFilters,
        isFetching: isFiltersFetching
    } = useGetDisplayFiltersQuery({
        ...search,
        filters: []
    }, {skip: !useFilters})

    const handlePageChange = (e: ChangeEvent<unknown>, pageNumber: number) => {
        setPage(pageNumber)
    }

    const isFetching = isSearchFetching || isFiltersFetching

    const searchResultControl = useSearchResultControl({searchResult: searchResult, page})

    useMemo(() => searchResultControl.resetMultiSelect(), [
        phrase,
        partNumberPhrase,
        labelPhrase,
        idPhrase,
        filters,
        itemTypes,
        organizations,
        notDefinedByOrganizations,
        technicalClassificationNodeId,
        isPublic,
        visibleByGuildId,
        managedByGuildId,
        guildId,
        itemListId])

    return {
        searchResult,
        searchFilters,
        search,
        page,
        handlePageChange,
        searchResultControl,
        isFetching,
        isSearchFetching,
        isFiltersFetching,
        fullSearchParams
    }
}

export type FullSearchResult = ReturnType<typeof useFullSearch>
